import { CustomModal } from 'components/molecules/Modal/Modal'
import { FC } from 'react'

// Components
import TextInput from 'components/molecules/TextInput/TextInput'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDayjs from '@mui/lab/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

// Logic
import useAdmitNewPatientLogic from './AdmitNewPatient.logic'

// Types
import { IAdmitNewPatient } from './AdmitNewPatient.types'

// Hook form
import { Controller } from 'react-hook-form'

// Utils
import dayjs from 'lib/dayjs'

// Style
import './AdmitNewPatient.scss'

const AdmitNewPatient: FC<IAdmitNewPatient> = (props) => {
  const { open, onClose } = props
  const { control, errors, isLoadingAdmitPatient, handleOnSubmit, reset } =
    useAdmitNewPatientLogic(props)

  const handleClose = () => {
    reset()
    onClose && onClose('', 'escapeKeyDown')
  }

  return (
    <CustomModal
      header="Admit New Patient"
      isLoadingState={isLoadingAdmitPatient}
      open={open}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
      handleOnSubmit={handleOnSubmit}
      icon={<></>}
    >
      <div className="admit-new-patient-container">
        <form onSubmit={handleOnSubmit}>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: {
                message: 'First name cannot be empty',
                value: true,
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <TextInput
                {...field}
                shrink
                value={value}
                handleChangeFormData={onChange}
                variant="outlined"
                placeholder="First Name"
                label="First Name*"
                error={!!errors.firstName}
                errorMessage={errors.firstName?.message}
                disabled={isLoadingAdmitPatient}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            rules={{
              required: {
                message: 'Last name cannot be empty',
                value: true,
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <TextInput
                {...field}
                shrink
                value={value}
                handleChangeFormData={onChange}
                variant="outlined"
                placeholder="Last Name"
                label="Last Name*"
                error={!!errors.lastName}
                errorMessage={errors.lastName?.message}
                disabled={isLoadingAdmitPatient}
              />
            )}
          />

          <Controller
            name="mrn"
            control={control}
            rules={{
              required: {
                message: 'MRN cannot be empty',
                value: true,
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <TextInput
                {...field}
                shrink
                value={value}
                handleChangeFormData={onChange}
                variant="outlined"
                placeholder="MRN"
                label="MRN*"
                error={!!errors.mrn}
                errorMessage={errors.mrn?.message}
                disabled={isLoadingAdmitPatient}
              />
            )}
          />
          <Controller
            name="dateOfBirth"
            control={control}
            rules={{
              required: {
                message: 'Date of birth cannot be empty',
                value: true,
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={value ? dayjs(value) : null}
                  onChange={(newValue: dayjs.Dayjs | null) => {
                    onChange(newValue ? newValue.toDate() : null)
                  }}
                  maxDate={dayjs()}
                  disabled={isLoadingAdmitPatient}
                  slotProps={{
                    textField: {
                      label: 'Date of Birth*',
                      error: !!errors.dateOfBirth,
                      helperText: errors.dateOfBirth?.message,
                      variant: 'outlined',
                      fullWidth: true,
                      sx: { mt: 1, backgroundColor: 'white' },
                      InputLabelProps: {
                        shrink: true,
                      },
                    },
                  }}
                  format="MM/DD/YYYY"
                  {...field}
                />
              </LocalizationProvider>
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <AutocompleteInput
                options={[
                  {
                    id: 'Male',
                    name: 'Male',
                  },
                  {
                    id: 'Female',
                    name: 'Female',
                  },
                ]}
                shrink
                onSelect={onChange}
                variant="outlined"
                placeholder="Gender"
                label="Gender"
                error={!!errors.gender}
                helperText={errors.gender?.message}
                disabled={isLoadingAdmitPatient}
                value={value}
                handleChangeFormData={(e) => onChange(e.target.value)}
                sx={{
                  mt: 1,
                }}
                {...field}
              />
            )}
          />
        </form>
      </div>
    </CustomModal>
  )
}

export default AdmitNewPatient
